import Loader from 'core/components/common/Loader'
import { ROUTE_PATHS } from 'core/constants/routePath'
import { UserContext } from 'core/contexts/UserContext'
import { useMyPermissions } from 'core/hooks/useMyPermissions'
import { ProfilesList } from 'lauria/components/profiles/ProfilesList'
import { ProfilesListMobile } from 'lauria/components/profiles/ProfilesListMobile'
import { KeycloakImportModal } from 'lauria/features/keycloakImport/KeycloakImportModal'
import { useProfilesList } from 'lauria/pages/profiles/useProfilesList'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

const PAGE_SIZE = 20

export const Profiles = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { user: currentUser } = useContext(UserContext)
  const { isBusinessReady, canManageProfiles, isSuperAdmin } =
    useMyPermissions()
  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false)

  const idClient = currentUser?.idClient
  const {
    rows,
    totalItems,
    page,
    pageSize,
    searchInput,
    setSearchInput,
    applySearch,
    setPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useProfilesList({ idClient, pageSize: PAGE_SIZE })

  if (isBusinessReady && !canManageProfiles) {
    navigate(ROUTE_PATHS.DASHBOARD)
  }

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader pageCenter />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-4">
        <div className="text-center max-w-[520px]">
          <div className="text-xl font-semibold">{t('profiles.error')}</div>
        </div>
        <button className="btn default" onClick={refetch}>
          {t('profiles.retry')}
        </button>
      </div>
    )
  }

  return (
    <>
      <div className="profiles-list-wrapper md:flex hidden relative">
        {isFetching && !isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <Loader />
          </div>
        )}
        <div className="profile-search w-full md:w-4/5">
          <div className="flex items-center gap-3 w-full">
            <input
              type="search"
              placeholder={t('profiles.search')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  applySearch()
                }
              }}
              className="border border-gray-300 p-2 rounded-[5px] w-[400px] h-[50px] text-xl"
            />
            <button
              onClick={applySearch}
              className="btn default"
              disabled={isLoading || isFetching}
            >
              {t('profiles.searchButton')}
              {(isLoading || isFetching) && (
                <span className="button-loader ml-2 align-middle"></span>
              )}
            </button>

            {isSuperAdmin && (
              <button
                onClick={() => setIsImportModalOpen(true)}
                className="btn default ml-auto"
                disabled={isLoading || isFetching || !idClient}
              >
                {t('profiles.importKeycloak.openButton')}
              </button>
            )}
          </div>
        </div>

        <ProfilesList
          users={rows}
          total={totalItems}
          currentPage={page}
          pageSize={pageSize}
          onChangePage={setPage}
        />
      </div>
      <div className="profiles-list-wrapper flex md:hidden relative">
        {isFetching && !isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <Loader />
          </div>
        )}
        <div className="w-full px-4 mb-4 flex justify-end">
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="btn default"
            disabled={isLoading || isFetching || !idClient}
          >
            {t('profiles.importKeycloak.openButton')}
          </button>
        </div>
        <ProfilesListMobile users={rows} />
      </div>

      {idClient && (
        <KeycloakImportModal
          open={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          idClient={idClient}
          onImported={refetch}
        />
      )}
    </>
  )
}
